import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { Icon } from '@/components/synapse/Icon';
import { cn } from '@/lib/utils';
import type { Patient } from '@/types/patients';
import { HighlightMatch } from './HighlightMatch';
import { FlagPills } from './FlagPills';
import { ageFromDob, sexLabelKey } from './patientDisplay';

interface PatientResultRowProps {
  patient: Patient;
  query?: string;
  variant: 'card' | 'row';
  active?: boolean;
  onSelect: () => void;
}

/**
 * A single search/recent result. Shows the patient's MRN (mono) — NOT the NIC.
 * Search returns plaintext only (the server never decrypts the NIC/history on
 * search), so the row identifies by the clinic-visible MRN; the NIC and any
 * clinical flags surface on the summary, which fetches the decrypted record.
 *
 * `card` variant is the mobile tile; `row` is the desktop master-list row with
 * a 2px left active border. Both are keyboard-focusable buttons with a ≥44px
 * tap target.
 */
export function PatientResultRow({
  patient,
  query = '',
  variant,
  active = false,
  onSelect,
}: PatientResultRowProps) {
  const { t } = useTranslation();
  const isCard = variant === 'card';
  const age = ageFromDob(patient.dateOfBirth);
  const sex = t(sexLabelKey(patient.sex));

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        isCard
          ? 'min-h-[44px] rounded-md border border-line bg-paper p-3 hover:bg-paper-2'
          : cn(
              'min-h-[44px] border-l-2 px-4 py-3 hover:bg-paper-2',
              active ? 'border-ink bg-paper-3' : 'border-transparent',
            ),
      )}
    >
      <InitialsAvatar name={patient.fullName} photoUrl={patient.photoUrl} size={isCard ? 44 : 36} />
      <div className="min-w-0 flex-1">
        <div className={cn('truncate font-semibold tracking-[-0.01em]', isCard ? 'text-[15px]' : 'text-sm')}>
          <HighlightMatch text={patient.fullName} query={query} />
        </div>
        <div className="mt-0.5 text-[12px] text-muted">
          <span>
            {sex}
            {age != null && (
              <>
                {' · '}
                {age} {t('patients.yrs')}
              </>
            )}
          </span>
          {patient.phone && (
            <>
              <span className="mx-1.5 text-muted-2">·</span>
              <span className="font-mono">
                <HighlightMatch text={patient.phone} query={query} />
              </span>
            </>
          )}
        </div>
        <div className="mt-0.5 font-mono text-[11.5px] text-muted-2">
          {t('patients.mrnLabel')}{' '}
          <HighlightMatch text={patient.mrn} query={query} />
        </div>
        {/* FlagPills renders only when allergy/condition data is present.
            Search results carry none (server returns plaintext only), so this
            is null here; it self-hides without adding empty DOM. */}
        <FlagPills />
      </div>
      <Icon name="chevron_right" size={16} className="shrink-0 text-muted-2" />
    </button>
  );
}
